import { generatePath } from 'react-router-dom';

import { AnimatePresence } from 'framer-motion';

import { Slide } from '@components/animators/slide';
import { AvatarInfo } from '@components/avatar-info';
import { Badge, BadgeVariant } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { useLocalStorageState } from '@components/component-helpers';
import { EmptyState } from '@components/empty-state';
import { UploadedFile } from '@components/file-field';
import { useDeleteModal } from '@components/generic-modals';
import { IconEdit } from '@components/icons/edit';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconTrash } from '@components/icons/trash';
import { IconTraveller } from '@components/icons/traveller';
import { LabelAvatarNameGroup } from '@components/label-avatar-name-group';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { StatusPiechartCircle } from '@components/piechart-circle/piechart-circle';
import { PopoverSelect } from '@components/popover-select';
import { Separator } from '@components/separator';
import { Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { formatNumber, hasNumericValue } from '@utils/numbers';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { TravellerAttributeTemplate } from 'api/attributes/requests';
import { useCompanyTraveller } from 'api/company-traveller/hooks';
import { CompanyTraveller } from 'api/company-traveller/requests';
import { useJourneys } from 'api/journeys/hooks';
import {
  useDeleteTravellerJourney,
  useGetTravellerJourneysStatus,
} from 'api/traveller-journeys/hooks';
import { TravellerJourneyStatusType } from 'api/traveller-journeys/types';
import { TravellerAttribute, TravellerJourneyStatus } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { usePermissions } from 'contexts/permissions-context';
import { routePaths } from 'ui/authenticated/navigation';

import { useBulkCompanyTravellerToJourneySidebar } from './add-to-journey';
import { useCompanyTravellerSidebar } from './form';

export const useCompanyTravellerProfileSidebar = () => {
  const sidebar = useSidebar(CompanyTravellerProfile, {
    variant: 'CONTENT_HEAVY',
    closeOnClickOutside: true,
  });
  return sidebar;
};

type CompanyTravellerProfileProps = {
  companyTravellerShortId: string;
  shouldAnimate?: boolean;
};

const CompanyTravellerProfile = (props: CompanyTravellerProfileProps) => {
  const { companyTravellerShortId, shouldAnimate } = props;
  const { data: companyTravellerAttributeTemplates = [] } = useCompanyTravellerAttributeTemplates();
  const { t } = useScopedTranslate(locale);
  const showEditSidebar = useCompanyTravellerSidebar();
  const showProfileSidebar = useCompanyTravellerProfileSidebar();
  const companyTravellerQuery = useCompanyTraveller(companyTravellerShortId ?? '');
  const companyTraveller = companyTravellerQuery.data;
  const journeysQuery = useJourneys('?ordering=title');
  const showAddSidebar = useBulkCompanyTravellerToJourneySidebar();

  const [tabIndex, setTabIndex] = useLocalStorageState({
    key: 'company-traveller-profile-open-tab',
    defaultValue: 0,
    valueAsNumber: true,
  });

  const { canManageJourneyTravellers } = usePermissions();

  const options = (journeysQuery.data ?? [])
    .filter((journey) => journey.hasPublishedJourney)
    .map((journey) => ({
      value: journey.shortId,
      label: journey.title,
      helpText: journey.description || t('noDescription'),
    }));

  if (!companyTraveller || !companyTravellerShortId) {
    return <RelativeSpinner />;
  }

  const tabs = [
    {
      content: (
        <Details
          companyTraveller={companyTraveller}
          attributeTemplates={companyTravellerAttributeTemplates}
          shouldAnimate={shouldAnimate}
        />
      ),
      label: t('details'),
      section: 'details',
    },
    {
      content: <Journeys companyTraveller={companyTraveller} />,
      tabCount: companyTraveller.travellerJourneyIds?.length,
      label: t('journeys'),
      section: 'journeys',
    },
  ];

  const selectedTraveller = [companyTraveller];

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('travellerProfile')}
        icon={IconTraveller}
        iconColor="elementBlue100"
        tabs={tabs}
        defaultIndex={tabIndex}
        onTabChange={setTabIndex}
      >
        {canManageJourneyTravellers && (
          <PopoverSelect
            trigger={
              <Button variant="ghost" text={t('addToJourney')} iconLeft={<IconJourneyItem />} />
            }
            width={pxToRem(400)}
            maxHeight={464}
            items={options}
            searchPlaceholder={`${t('addToJourney')}...`}
            clearSelected
            onChange={(selectedOptions) => {
              selectedOptions.length > 0 &&
                showAddSidebar({
                  journeyShortId: selectedOptions[0],
                  selectedTravellers: selectedTraveller.map((traveller) => ({
                    companyTravellerShortId: traveller.shortId,
                  })),
                  isSelectAll: false,
                  onCancel: () =>
                    showProfileSidebar({
                      companyTravellerShortId,
                    }),
                });
            }}
            placement="bottom-start"
          />
        )}
        <Button
          variant="ghost"
          iconLeft={<IconEdit />}
          text={t('core.edit')}
          onClick={() =>
            showEditSidebar({
              companyTravellerShortId,
              onBacktrack: () => showProfileSidebar({ companyTravellerShortId }),
            })
          }
        />
      </Sidebar.SimpleHeader>
    </Sidebar>
  );
};

type DetailsProps = {
  companyTraveller: CompanyTraveller;
  attributeTemplates: TravellerAttributeTemplate[] | undefined;
  shouldAnimate?: boolean;
};

const Details = (props: DetailsProps) => {
  const { companyTraveller, attributeTemplates = [], shouldAnimate } = props;
  const { t } = useScopedTranslate(locale);

  return (
    <Sidebar.Content>
      <AnimatePresence initial={shouldAnimate} exitBeforeEnter>
        <Slide key="traveller-profile" offset={30}>
          <Stack space={6}>
            <AvatarInfo
              title={companyTraveller.name}
              subtitles={[companyTraveller.email, companyTraveller.phone]}
              src={companyTraveller.picture}
            />
            {companyTraveller.dateCreated && (
              <Stack orientation="horizontal" space={2} align="center">
                <TextStatusIndicator text={t('created')} variant="info" />
                <Text color="textHelper" font="regular14" text="•" />
                <Text
                  color="textHelper"
                  font="regular14"
                  text={abbreviateDate(companyTraveller.dateCreated, 'en', 'datetime')}
                />
              </Stack>
            )}
            <Separator />
            <Stack space={2}>
              <TravellerInfoText title={t('core.fullName')} text={companyTraveller.name} />
              <TravellerInfoText title={t('core.email')} text={companyTraveller.email} />
              <TravellerInfoText
                title={t('core.phone')}
                text={companyTraveller.phone ?? t('core.not_set')}
                isNotSet={!companyTraveller.phone}
              />
              {attributeTemplates.map((attributeTemplate) => {
                const attributeValue = companyTraveller.companyTravellerAttributes.find(
                  (companyAttr) => companyAttr.attributeShortId === attributeTemplate.shortId
                );
                return (
                  attributeTemplate.isActive && (
                    <ViewCompanyTravellerAttributes
                      key={attributeTemplate.id}
                      attributeTemplate={attributeTemplate}
                      travellerAttribute={attributeValue}
                    />
                  )
                );
              })}
            </Stack>
          </Stack>
        </Slide>
      </AnimatePresence>
    </Sidebar.Content>
  );
};

const TravellerInfoText = ({
  title,
  text,
  isNotSet,
}: {
  title: string;
  text: string;
  isNotSet?: boolean;
}) => {
  return (
    <div>
      <Text font="semibold14" text={title} />
      <Text font="regular14" text={text} color={isNotSet ? 'textHelper' : 'textPrimary'} />
    </div>
  );
};

export const ViewCompanyTravellerAttributes = ({
  attributeTemplate,
  travellerAttribute,
}: {
  attributeTemplate: TravellerAttributeTemplate;
  travellerAttribute?: TravellerAttribute;
}) => {
  const { t } = useScopedTranslate();
  const showFilePreviewModal = usePreviewFileModal();
  const userQuery = useUsers();
  if (userQuery.isLoading) return <Spinner />;

  const usersData = userQuery.data;
  const attributeValue = travellerAttribute?.value;
  const attrMultiValues = travellerAttribute?.multiOptionValues ?? [];
  const attrFiles = travellerAttribute?.files ?? [];
  const isEmptyValue =
    attributeValue === null ||
    attributeValue === undefined ||
    (typeof attributeValue === 'string' && attributeValue.trim() === '');

  const isAttributeNotSet =
    !travellerAttribute || (isEmptyValue && attrFiles.length === 0 && attrMultiValues.length === 0);

  const renderAttribute = (travellerAttribute: TravellerAttribute) => {
    switch (attributeTemplate.attributeType) {
      case 'date':
        const date = abbreviateDate(travellerAttribute.value, 'en');
        return (
          <Text
            font="regular14"
            text={date ?? t('core.not_set')}
            color={date ? 'textPrimary' : 'textHelper'}
          />
        );
      case 'user':
        const user = usersData?.find((u) => u.shortId === travellerAttribute.value.shortId);
        return (
          <LabelAvatarNameGroup
            label={attributeTemplate.templateTitle}
            name={user?.name}
            image={user?.picture}
            hideLabel
          />
        );
      case 'single_select':
        return (
          <Text
            font="regular14"
            text={travellerAttribute.value.label ?? t('core.not_set')}
            color={travellerAttribute.value.label ? 'textPrimary' : 'textHelper'}
          />
        );
      case 'multi_select':
        return (
          <>
            {travellerAttribute.multiOptionValues.map((option) => (
              <Text
                key={option.value}
                font="regular14"
                text={option.label}
                color={option.label ? 'textPrimary' : 'textHelper'}
              />
            ))}
          </>
        );
      case 'file':
        const file = travellerAttribute.files[0];
        return (
          file && (
            <UploadedFile
              name={file.name}
              path={file.src}
              onPreview={() => {
                showFilePreviewModal({
                  formFile: file,
                  filesList: [file],
                });
              }}
            />
          )
        );
      case 'file_multi':
        const files = travellerAttribute.files;
        return (
          <Stack space={2}>
            {files?.map((file, index) => (
              <UploadedFile
                key={index}
                name={file.name}
                path={file.src}
                onPreview={() => {
                  showFilePreviewModal({
                    formFile: file,
                    filesList: files,
                  });
                }}
              />
            ))}
          </Stack>
        );
      case 'number':
      case 'decimal':
        const hasValue = hasNumericValue(travellerAttribute.value);
        const formattedNumber = hasValue ? formatNumber(travellerAttribute.value) : undefined;
        return (
          <Text
            font="regular14"
            text={formattedNumber ?? t('core.not_set')}
            color={hasValue ? 'textPrimary' : 'textHelper'}
          />
        );
      default:
        return <Text font="regular14" text={travellerAttribute.value} />;
    }
  };
  const content = isAttributeNotSet ? (
    <Text font="regular14" text={t('core.not_set')} color="textHelper" />
  ) : (
    renderAttribute(travellerAttribute)
  );

  return (
    <div data-testid="traveller-attribute-row">
      <Text font="semibold14" text={attributeTemplate.templateTitle} />
      {content}
    </div>
  );
};

const Journeys = ({ companyTraveller }: { companyTraveller: CompanyTraveller }) => {
  const { t } = useScopedTranslate(locale);
  const travellerJourneysQuery = useGetTravellerJourneysStatus(
    companyTraveller.travellerJourneyIds,
    companyTraveller.shortId
  );
  const companyTravellerJourneys = travellerJourneysQuery.data;

  if (travellerJourneysQuery.isLoading) {
    return <RelativeSpinner />;
  }

  return (
    <>
      {companyTravellerJourneys?.length === 0 ? (
        <Sidebar.Content>
          <EmptyState
            variant="small"
            icon={<IconJourneyItem size={40} />}
            header={t('noJourneys')}
            content={t('noJourneysDescription')}
            skipMargin
          />
        </Sidebar.Content>
      ) : (
        <Sidebar.Content>
          <Stack space={2}>
            {companyTravellerJourneys?.map((journey) => (
              <JourneyCard key={`${journey.journeyId}-${journey.id}`} journey={journey} />
            ))}
          </Stack>
        </Sidebar.Content>
      )}
    </>
  );
};

type StatusMapType = Record<TravellerJourneyStatus, { text: string; tagVariant: BadgeVariant }>;

const JourneyCard = ({ journey }: { journey: TravellerJourneyStatusType }) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const { canManageJourneyTravellers } = usePermissions();
  const removeFromJourneyMutation = useDeleteTravellerJourney({
    id: journey.id,
    journey: journey.journeyId,
  });

  const statusMap: StatusMapType = {
    not_started: {
      text: t('notStarted'),
      tagVariant: 'indigo',
    },
    paused: {
      text: t('paused'),
      tagVariant: 'neutral',
    },
    in_progress: {
      text: t('inProgress'),
      tagVariant: 'orange',
    },
    completed: {
      text: t('completed'),
      tagVariant: 'green',
    },
  };

  const status = statusMap[journey.status];

  const numNotStarted =
    journey.numActions -
    journey.numCompleted -
    journey.numInProgress -
    journey.numHalted -
    journey.numSkipped -
    journey.numToBeStarted;

  const progress: { color: Color; value: number }[] = [
    {
      color: 'elementGreen100',
      value: journey.numCompleted,
    },
    {
      color: 'elementOrange100',
      value: journey.numInProgress,
    },
    {
      color: 'elementIndigo100',
      value: journey.numToBeStarted,
    },
    {
      color: 'elementPurple50',
      value: journey.numHalted,
    },
    {
      color: 'elementPurple50',
      value: journey.numSkipped,
    },
    {
      color: 'uiTrans20',
      value: numNotStarted,
    },
  ];

  const journeysUrl = generatePath(routePaths.traveller, {
    workspaceId: journey.workspaceId,
    journeyId: journey.journeyId,
    travellerId: journey.id,
    '*': null,
  });

  return (
    <Card
      space={4}
      interactive
      onClick={() => window.open(journeysUrl, '_blank')}
      style={{ cursor: 'pointer' }}
    >
      <Stack space={2} orientation="horizontal" align="center" justify="space-between">
        <Stack space={3} align="center" orientation="horizontal">
          <IconJourneyItem />
          <div>
            <Text text={journey.journeyTitle} font="semibold14" />
            <Text text={journey.journeyDescription} font="regular12" color="textSecondary" />
            <Text
              font="regular12"
              color="textSecondary"
              text={t('added', {
                date_added: abbreviateDate(journey.dateCreated, 'en', 'datetime'),
              })}
            />
          </div>
        </Stack>
        <Stack space={4} orientation="horizontal" align="center">
          <Badge variant={status.tagVariant} text={status.text} />
          <StatusPiechartCircle size={3} sections={progress} status={journey.status} />
          <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
            <Menu.Item
              label={
                <Stack space={1} orientation="horizontal" align="center">
                  {t('openJourney')} <IconOpenNewTab size={16} />
                </Stack>
              }
              icon={<IconJourneyItem />}
              onSelect={() => window.open(journeysUrl, '_blank')}
            />
            {canManageJourneyTravellers && (
              <Menu.Item
                label={t('removeFromJourney')}
                icon={<IconTrash />}
                variant="danger"
                onSelect={() =>
                  showDeleteModal({
                    title: t('removeTraveller'),
                    description: t('areYouSureDelete'),
                    buttonText: t('core.remove'),
                    hasUserConfirmation: true,
                    onConfirm: () => removeFromJourneyMutation.mutateAsync(),
                  })
                }
              />
            )}
          </Menu>
        </Stack>
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    travellerProfile: 'Traveller Profile',
    details: 'Details',
    journeys: 'Journeys',
    addToJourney: 'Add to Journey',
    noJourneys: 'No Journeys',
    noJourneysDescription: 'This traveller is not in any journeys',
    created: 'Created',
    notStarted: 'Not Started',
    paused: 'Paused',
    inProgress: 'In Progress',
    completed: 'Completed',
    openJourney: 'Open Journey',
    removeFromJourney: 'Remove from Journey',
    added: 'Added: {date_added}',
    noDescription: 'No description',
    removeTraveller: 'Remove traveller',
    areYouSureDelete:
      'Are you sure you want to remove the following traveller from this journey? All journey details and progress will be lost',
    download: 'Download',
  },
};
